import React from 'react';
import { action, observable, toJS, computed, makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { OldInput } from 'src/domains/layouts/shared/formComponents/input/OldInput';
import { WithdrawalHistory } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory/WithdrawalHistory';
import { Dropdown } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/Dropdown/Dropdown';
import {
    DROPDOWN_AMOUNT_OPTIONS_FIAT,
    DROPDOWN_AMOUNT_OPTIONS_BTC,
    WITHDRAWAL_ACTIONS_OPTIONS,
    DROPDOWN_REJECT_OPTIONS,
} from 'src/components/FraudAndCompliance/AutomaticWithdrawals/SelectionOptions';
import { FormInputState as FormInputStateOld, FormModel } from 'src_common/common/mobx-utils/Form';
import { AutomaticWithdrawalState, RuleType } from 'src/AppState/AutomaticWithdrawalState';
import { SelectMultiple } from 'src/components/FormComponents/SelectMultiple/SelectMultiple';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';

import {
    GridBodyRow,
    GridRow,
    GridItemRow,
    SmallerItemRow,
    GridItemDropDown,
    ItemNotes,
    ButtonWrapper,
} from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/GridRow.style';
import { Amount } from 'src_common/common/amount/Amount';
import { WithdrawalRuleModelTypeLKType } from 'src/api/config/automaticWithdrawal/getWithdrawalRulesLK';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { formatCardDetails } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/Pending/WithdrawalPendingRow/WithdrawalPendingRow';

type FilterType = 'approve' | 'reject' | 'delete';

const convertToActionType = (value: string): FilterType | null => {
    if (value === 'approve' || value === 'reject' || value === 'delete') {
        return value;
    }

    return null;
};
interface SubmitRuleType {
    id: number;
    card: string;
    accountId: number;
    ruleType: RuleType;
    amount: string | null;
    reason: Array<string> | null;
    note: string;
}
interface RulesRowPropsType {
    ruleId: number;
    selectedTab: RuleType;
    models: ModelsState;
}

class State {
    @observable public isProcessed: boolean = false;

    public readonly automaticWithdrawalState: AutomaticWithdrawalState;
    public readonly ruleId: number;
    public readonly selectedTab: RuleType;

    public readonly amountValue: FormInputState<string, Amount>;

    public readonly reasonReject: FormInputStateOld<Array<string>>;
    public readonly reasonRejectModel: FormModel<Array<string>>;
    public readonly reasonRejectOptions: Record<string, string>;
    public readonly actionTypeModel: FormModel<string>;
    public readonly actionType: FormInputStateOld<string>;
    public readonly notesTextModel: FormModel<string | null>;
    public readonly notesText: FormInputStateOld<string>;
    public constructor(
        automaticWithdrawalState: AutomaticWithdrawalState,
        ruleId: () => number,
        selectedTab: () => RuleType
    ) {
        this.automaticWithdrawalState = automaticWithdrawalState;
        this.ruleId = ruleId();
        this.selectedTab = selectedTab();

        this.amountValue = FormInputState.new('').map((newAmount) => {
            if (newAmount === '') {
                return Result.createError('Incorrect number');
            }

            try {
                const convertedNewAmount = new Amount(newAmount);

                if (convertedNewAmount.isLessThanZero() || convertedNewAmount.isEqualWith(new Amount('0'))) {
                    return Result.createError('Incorrect number');
                }

                return Result.createOk(convertedNewAmount);
            } catch (error) {
                return Result.createError('Incorrect number');
            }
        });

        this.reasonReject = new FormInputStateOld([], false);
        this.reasonRejectModel = this.reasonReject.toModel();
        this.reasonRejectOptions = DROPDOWN_REJECT_OPTIONS;

        this.actionType = new FormInputStateOld('delete', false);
        this.actionTypeModel = this.actionType.toModel();
        this.notesText = new FormInputStateOld('', false);
        this.notesTextModel = this.notesText.toModel();

        this.setInitValues();
        makeAutoObservable(this);
    }

    @computed public get ruleModel(): WithdrawalRuleModelTypeLKType {
        const model = this.automaticWithdrawalState.getItemRule(this.ruleId, this.selectedTab);
        if (model === null) {
            return {
                id: 0,
                universe: 'star',
                card: '0000',
                accountId: 0,
                ruleType: 'delete',
                amount: '0',
                reason: [],
                note: '',
                expires: '',
            };
        }

        return model;
    }

    public setInitValues = (): void => {
        this.amountValue.setValue(this.ruleModel.amount ?? '0');
        this.reasonReject.setValue(this.ruleModel.reason ?? []);
        this.notesText.setValue(this.ruleModel.note ?? '');
        this.actionType.setValue(this.ruleModel.ruleType);
    };

    public onChangeAmount = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        this.amountValue.setValue(value);
    };

    public onChangeActionType = (event: React.ChangeEvent<HTMLSelectElement>): void | null => {
        const value = event.target.value;
        this.actionType.setValue(value);
    };

    @computed public get isButtonDisabled(): boolean {
        const actionType = this.actionType.value;
        const noteText = this.notesText.value;
        const amount = this.amountValue.value;

        if (actionType === this.ruleModel.ruleType && noteText !== this.ruleModel.note) {
            return false;
        } else if (actionType === 'approve' && amount !== this.ruleModel.amount) {
            return false;
        } else if (actionType === 'reject' && this.differentSelectMultiple) {
            return false;
        } else if (actionType === 'delete') {
            return false;
        } else {
            return true;
        }
    }

    @computed private get differentSelectMultiple(): boolean {
        const newValue = JSON.stringify(this.reasonReject.value);
        const oldValue = JSON.stringify(this.ruleModel.reason);
        if (newValue === '[]') {
            return false;
        }

        return oldValue === newValue ? false : true;
    }

    public onChangeNote = (): void => {
        const value = this.notesText.value;
        this.notesText.setValue(value);
    };

    public onSubmit = async (): Promise<void> => {
        const actionTypeLocal = this.actionType.value;
        const selectValue = actionTypeLocal === '' ? this.ruleModel.ruleType : actionTypeLocal;

        const selectAction = convertToActionType(selectValue);

        if (selectAction === 'delete') {
            await this.execDelete();
        }

        if (selectAction === 'approve' || selectAction === 'reject') {
            await this.editRule(selectAction);
        }

        if (selectAction === null) {
            console.error(`option not supported: ${selectValue}`);
            return;
        }
    };

    public async editRule(selectAction: RuleType): Promise<void> {
        const reasonRejectValueLocal = toJS(this.reasonReject.value);
        const amountValueLocal = this.amountValue.value;
        const note = this.notesText.value;

        const mutualPart = {
            id: this.ruleModel.id,
            card: this.ruleModel.card,
            accountId: this.ruleModel.accountId,
            note: note,
        };

        const RequestArr: Record<RuleType, SubmitRuleType> = {
            approve: {
                ...mutualPart,
                ruleType: 'approve',
                amount: amountValueLocal,
                reason: null,
            },
            reject: {
                ...mutualPart,
                ruleType: 'reject',
                amount: '0',
                reason: reasonRejectValueLocal,
            },
        };
        const requestBody = RequestArr[selectAction];

        await this.execAction(requestBody);
        return;
    }

    @action public async execAction(requestBody: SubmitRuleType): Promise<void> {
        this.isProcessed = true;
        await this.automaticWithdrawalState.execSetOrUpdateRule(requestBody);
        this.isProcessed = false;
    }

    @action public async execDelete(): Promise<void> {
        const body = {
            id: this.ruleModel.id,
            accountId: this.ruleModel.accountId,
        };

        this.isProcessed = true;
        await this.automaticWithdrawalState.execDelete(body);
    }
}

export const RulesRow = observer(({ ruleId, selectedTab, models }: RulesRowPropsType) => {
    const { automaticWithdrawalState } = useAppStateContext();

    const [state] = React.useState(
        () =>
            new State(
                automaticWithdrawalState,
                () => ruleId,
                () => selectedTab
            )
    );

    const customerData = models.getUserModel(state.ruleModel.accountId).basicData.data;
    if (customerData === null) {
        return null;
    }
    const { title, name, currency } = customerData;

    const typeValue = state.actionType.value === '' ? state.ruleModel.ruleType : state.actionType.value;
    return (
        <GridBodyRow isProcessed={state.isProcessed}>
            <GridRow data-test='withdrawal-row'>
                <SmallerItemRow> {state.ruleModel.accountId}</SmallerItemRow>
                <SmallerItemRow> {formatCardDetails(state.ruleModel.card, state.ruleModel.expires)}</SmallerItemRow>
                <SmallerItemRow> {`${title} ${name}`}</SmallerItemRow>
                <GridItemRow>
                    {typeValue === 'reject' ? (
                        <SelectMultiple
                            name='reason'
                            placeholder='Please select...'
                            minSelections={0}
                            maxSelections={9}
                            state={state.reasonReject}
                            model={state.reasonRejectModel}
                            options={state.reasonRejectOptions}
                            viewType='small'
                        />
                    ) : (
                        <Dropdown
                            onChange={state.onChangeAmount}
                            dropDownvalue={state.amountValue.value}
                            options={currency === 'BTC' ? DROPDOWN_AMOUNT_OPTIONS_BTC : DROPDOWN_AMOUNT_OPTIONS_FIAT}
                            currency={currency}
                            placeholder='Select amount...'
                        />
                    )}
                </GridItemRow>
                <GridItemDropDown>
                    <Dropdown
                        onChange={state.onChangeActionType}
                        dropDownvalue={state.actionType.value}
                        options={WITHDRAWAL_ACTIONS_OPTIONS}
                    />
                    <ButtonWrapper
                        btnColor='confirm'
                        isDisabled={state.isButtonDisabled}
                        onClick={state.onSubmit}
                        dataTest='submit-processed'
                    >
                        Submit
                    </ButtonWrapper>
                </GridItemDropDown>

                <WithdrawalHistory accountId={state.ruleModel.accountId} />
                <ItemNotes data-test='withdrawal-note'>
                    <OldInput
                        onChange={state.onChangeNote}
                        formInputState={state.notesText}
                        model={state.notesTextModel}
                        placeholder='Add a note...'
                    />
                </ItemNotes>
            </GridRow>
        </GridBodyRow>
    );
});
